import { Directive, Input, OnInit, inject } from '@angular/core'
import { ElderEnumTranslationService, ElderSelectComponent } from '@elderbyte/ngx-starter'
import { DistributionMode, PackagingStrategy } from './work-center'
import { BehaviorSubject, Observable } from 'rxjs'
import { LoggerFactory } from '@elderbyte/ts-logger'

@Directive({
  selector: '[appPackagingDistributionModeSelect]',
})
export class PackagingDistributionModeSelectDirective implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private possibleModesByStrategy = new Map<PackagingStrategy, DistributionMode[]>([
    [PackagingStrategy.STACK_HORIZONTALLY, [DistributionMode.FILL_UP, DistributionMode.EVEN]],
    [PackagingStrategy.STACK_ORDERED, [DistributionMode.FILL_UP]],
  ])

  private _parentStrategy$ = new BehaviorSubject<PackagingStrategy>(null)
  private select = inject(ElderSelectComponent)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const enumTranslationService = inject(ElderEnumTranslationService)
    this.select.placeholder = 'packaging.distributionMode.title'
    this.select.displayProperty = 'displayValue'

    this.select.dataSource = enumTranslationService.enumDataSource(
      DistributionMode,
      'packaging.distributionMode',
      false
    )

    this.parentStrategy$.subscribe({
      next: (strategy) => {
        if (strategy) {
          this.select.isOptionDisabledFn = (option) => this.optionIsDisabled(option)
        }
      },
      error: (err) => this.log.error(err),
    })
  }

  public ngOnInit(): void {
    this.checkAndSetAllowedDistributionMode(this.select.entity)
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set parentStrategy(strategy: PackagingStrategy) {
    this._parentStrategy$.next(strategy)
    this.checkAndSetAllowedDistributionMode(this.select.entity)
  }

  public get parentStrategy(): PackagingStrategy {
    return this._parentStrategy$.getValue()
  }

  public get parentStrategy$(): Observable<PackagingStrategy> {
    return this._parentStrategy$.asObservable()
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private optionIsDisabled(option: any): boolean {
    const mode = option.value
    const modeIsAllowed = this.possibleModesByStrategy.get(this.parentStrategy).includes(mode)
    return !this.parentStrategy || !modeIsAllowed
  }

  private checkAndSetAllowedDistributionMode(entity: any): void {
    if (!entity || this.optionIsDisabled(entity)) {
      const firstValidMode = this.possibleModesByStrategy
        .get(this.parentStrategy)
        ?.find((mode) => !!mode)
      if (firstValidMode) {
        this.select.value = firstValidMode
      } else {
        this.select.clear(null)
      }
    }
  }
}
